import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { readAllOpsNotifications, readOpsNotification } from '../../../api/twoDays/ops.api';
import TdButton from '../../../components/TwoDays/Button';
import { Select } from '../../../components/TwoDays/Modal';
import { cn } from '../../../components/TwoDays/model/derive';
import { KST, formatDue } from '../../../components/TwoDays/model/time';

/**
 * Ops Notifications (알림 목록 v2 §C·E) — 워크보드 Notifications 와 같은 모양, 운영진 공통 목록.
 *   C1 Lead inbox 새 인계 · C2 운영진 질문에 답글 · C3 팀장에게 온 질문 · C4 태스크 거절
 * 누르면 읽음 처리하고 Lead inbox 리뷰(댓글이면 그 댓글) 또는 Setup › Tasks 로 간다.
 */
const OpsNotifications = () => {
  const { opsNotes, setOpsNotes, dispatch } = useOutletContext();
  const navigate = useNavigate();
  const [team, setTeam] = useState('all');
  const list = opsNotes ?? [];

  const groups = [...new Set(list.map((n) => n.projectId))].map((pid) => ({
    id: pid,
    name: list.find((n) => n.projectId === pid).projectName,
    teams: [
      ...new Map(
        list.filter((n) => n.projectId === pid).map((n) => [n.teamId, n.teamName]),
      ).entries(),
    ],
  }));
  const shown = list.filter((n) => team === 'all' || String(n.teamId) === team);
  const unread = shown.filter((n) => !n.readAt);

  const open = (n) => {
    if (!n.readAt)
      readOpsNotification(n.id)
        .then(setOpsNotes)
        .catch(() => {});
    if (n.linkTo === 'inbox' && n.taskId) {
      navigate(`/2days/ops/leads/${n.taskId}${n.commentId ? `#comment-${n.commentId}` : ''}`);
    } else if (n.linkTo === 'messages') {
      navigate('/2days/ops/messages');
    } else {
      // 거절된 태스크 — 그 프로젝트의 Tasks 단계에서 재배정
      dispatch({ type: 'select', id: n.projectId });
      navigate('/2days/ops/setup?step=tasks');
    }
  };
  const readAll = () => {
    if (team === 'all') {
      readAllOpsNotifications()
        .then(setOpsNotes)
        .catch(() => {});
    } else {
      Promise.all(unread.map((n) => readOpsNotification(n.id)))
        .then((r) => r.length && setOpsNotes(r[r.length - 1]))
        .catch(() => {});
    }
  };

  return (
    <div className='flex max-w-[1080px] flex-col gap-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
          Notifications
        </h1>
        <div className='flex items-center gap-3'>
          <Select
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            aria-label='Team'
            className='w-[180px]'
          >
            <option value='all'>All teams</option>
            {groups.map((g) => (
              <optgroup key={g.id} label={g.name}>
                {g.teams.map(([id, name]) => (
                  <option key={id} value={String(id)}>
                    {name}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          <TdButton variant='soft' disabled={unread.length === 0} onClick={readAll}>
            Mark all as read
          </TdButton>
        </div>
      </div>

      {opsNotes == null ? (
        <p className='py-10 text-center text-[14px] text-text-secondary'>Loading…</p>
      ) : shown.length === 0 ? (
        <p className='rounded-2xl border border-dashed border-grayscale-gray03 px-4 py-10 text-center text-[14px] text-text-secondary'>
          No notifications yet.
        </p>
      ) : (
        <ul className='divide-y divide-green-deep03/[0.08] overflow-hidden rounded-2xl bg-grayscale-gray00 shadow-4'>
          {shown.map((n) => (
            <li key={n.id}>
              <button
                type='button'
                onClick={() => open(n)}
                className='flex w-full items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-green-deep03/[0.03]'
              >
                <span
                  aria-label={n.readAt ? 'Read' : 'Unread'}
                  className={cn(
                    'mt-[7px] h-2 w-2 shrink-0 rounded-full',
                    n.readAt ? 'bg-transparent' : 'bg-functional-failed03',
                  )}
                />
                <span className='flex min-w-0 flex-1 flex-col gap-1'>
                  <span className='text-[15px] leading-[150%] text-text-primary'>
                    <span className='text-text-secondary'>{n.teamName} · </span>
                    {n.text}
                  </span>
                  <span className='text-[13px] text-text-secondary'>
                    {formatDue(n.createdAt, KST)} KST
                    {groups.length > 1 && ` · ${n.projectName}`}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default OpsNotifications;
